import { useState } from 'react'
import { AlertCircle, CheckCircle2, ShieldAlert } from 'lucide-react'
import { useHangingLeases, useResolveHangingLease, type HangingLease } from '@/api/experiments'
import { ApiError } from '@/api/client'
import Card from '@/components/Card'
import SectionHead from '@/components/SectionHead'

/**
 * Ab wann darf geprüft werden — und wenn noch nicht, ab wann und warum?
 *
 * Zwei Termine können gelten. `resolvableAt` ist die Karenz seit dem Erwerb der
 * Lease. `secondResolvableAt` kommt dazu, sobald eine erste Baseline-Beobachtung
 * vorliegt: dann wird die zweite Prüfung erst eine Karenz nach jener Beobachtung
 * angenommen. Maßgeblich ist der SPÄTERE von beiden — sonst stünde die
 * Schaltfläche offen, während der Server noch ablehnt, und der Operator liefe in
 * wiederholte Absagen.
 *
 * Fehlt oder verunglückt ein Zeitwert, gilt: keine Schaltfläche. Kein lesbarer
 * Termin heißt „unklar", nicht „sofort erlaubt".
 *
 * `jetzt` ist die geschätzte SERVERZEIT, nicht `Date.now()`: die Termine
 * entstehen aus der Uhr der Datenbank, und der Server entscheidet gegen
 * dieselbe. Ginge die Uhr dieses Rechners vor, erschiene die Schaltfläche zu
 * früh und der Klick brächte nur eine Absage; ginge sie nach, bliebe eine
 * längst prüfbare Lease gesperrt.
 */
function pruefbarkeit(
  lease: HangingLease,
  jetzt: number,
): { pruefbar: true } | { pruefbar: false; ab: Date | null; ersteBeobachtungLiegtVor: boolean } {
  const ersterMs = lease.resolvableAt ? Date.parse(lease.resolvableAt) : NaN

  // FEHLENDES Feld ist NICHT dasselbe wie `null`. `null` heisst „der Server hat
  // nachgesehen, es gibt keine Beobachtung". Ein fehlendes Feld heisst „diese
  // Antwort weiss nichts darüber" — etwa bei Versionsversatz zwischen
  // Oberfläche und Server. Behandelte man beides gleich, erschiene die
  // Schaltfläche, obwohl der Server eine Beobachtung kennt und ablehnen würde.
  const feldFehlt = !('secondResolvableAt' in lease) || lease.secondResolvableAt === undefined
  if (feldFehlt) return { pruefbar: false, ab: null, ersteBeobachtungLiegtVor: false }

  const hatBeobachtung = lease.secondResolvableAt !== null
  const zweiterMs = lease.secondResolvableAt ? Date.parse(lease.secondResolvableAt) : NaN

  if (!Number.isFinite(ersterMs)) return { pruefbar: false, ab: null, ersteBeobachtungLiegtVor: hatBeobachtung }
  if (hatBeobachtung && !Number.isFinite(zweiterMs)) return { pruefbar: false, ab: null, ersteBeobachtungLiegtVor: true }

  const massgeblichMs = hatBeobachtung ? Math.max(ersterMs, zweiterMs) : ersterMs
  if (massgeblichMs > jetzt) {
    return { pruefbar: false, ab: new Date(massgeblichMs), ersteBeobachtungLiegtVor: hatBeobachtung }
  }
  return { pruefbar: true }
}

const alsZeit = (d: Date) => d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })

export default function OperatorReconcile() {
  const { data, isLoading, isError, error } = useHangingLeases()
  const resolveLease = useResolveHangingLease()

  const [results, setResults] = useState<Record<number, { type: 'success' | 'error' | 'demo' | 'forbidden', message: string, originalLease?: HangingLease }>>({})

  const handleResolve = async (lease: HangingLease) => {
    try {
      const response = await resolveLease.mutateAsync(lease.id)
      const readAt = new Date(response.evidence.readAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
      let proofMsg = ''
      if (response.evidence.verdict === 'forward') {
        proofMsg = `Amazon steht auf dem Experiment-Wert — der Write hat stattgefunden (gelesen ${readAt}, Keyword ${response.evidence.amazonKeywordId}). Lease auf \`${response.leaseStatus}\` gesetzt.`
      } else if (response.evidence.verdict === 'baseline') {
        proofMsg = `Amazon steht auf dem Ausgangswert — der Write ist ausgeblieben (gelesen ${readAt}, Keyword ${response.evidence.amazonKeywordId}). Lease auf \`${response.leaseStatus}\` gesetzt.`
      } else {
        proofMsg = `Unbekannter Fall: "${response.evidence.verdict}" (gelesen ${readAt}, Keyword ${response.evidence.amazonKeywordId}). Lease auf \`${response.leaseStatus}\` gesetzt.`
      }
      
      setResults(prev => ({ ...prev, [lease.id]: { type: 'success', message: proofMsg, originalLease: lease } }))
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 423 && err.body?.error?.[0]?.field === 'READ_ONLY_MODE') {
          setResults(prev => ({ ...prev, [lease.id]: { type: 'demo', message: 'Plattform läuft im Demo-Modus: Aktionen sind gesperrt.' } }))
        } else if (err.status === 403) {
          setResults(prev => ({ ...prev, [lease.id]: { type: 'forbidden', message: 'Fehlende Berechtigung für diese Aktion.' } }))
        } else if (err.status === 409) {
          setResults(prev => ({ ...prev, [lease.id]: { type: 'error', message: err.message } }))
        } else {
          setResults(prev => ({ ...prev, [lease.id]: { type: 'error', message: err.message } }))
        }
      } else {
        setResults(prev => ({ ...prev, [lease.id]: { type: 'error', message: 'Unbekannter Fehler aufgetreten.' } }))
      }
    }
  }

  // Eine 200-Antwort ohne verwertbares `leases` ist KEIN Leerzustand. Vorher
  // machte `data?.leases || []` daraus "Alles in Ordnung" — auf einer Seite,
  // deren einziger Zweck das Sichtbarmachen offener Faelle ist, wird aus
  // "ich konnte den Stand nicht lesen" so ein "es gibt nichts zu tun".
  const antwortUnbrauchbar = !isLoading && !isError && !Array.isArray(data?.leases)
  const serverLeases = Array.isArray(data?.leases) ? data.leases : []

  // Die geschaetzte Serverzeit: eigene Uhr plus der beim Empfang gemessene
  // Versatz. `null` heisst „der Server hat keine Zeit mitgeschickt" — dann wird
  // nichts geraten und es bleibt bei der eigenen Uhr, also beim Verhalten von
  // vorher. Bewusst als Funktion und nicht als Konstante: sie wird je Zeile
  // waehrend des Renderns gerufen und muss dabei mitlaufen, nicht auf dem
  // Zeitpunkt des Renderbeginns einfrieren.
  const versatzMs = data?.offsetMs ?? 0
  const jetztLautServer = () => Date.now() + versatzMs
  
  // Erfolgreich aufgelöste Leases im UI behalten
  const displayLeases = [...serverLeases]
  Object.values(results).forEach(res => {
    if (res.type === 'success' && res.originalLease && !displayLeases.find(l => l.id === res.originalLease!.id)) {
      displayLeases.push(res.originalLease)
    }
  })
  
  displayLeases.sort((a, b) => b.id - a.id)

  return (
    <div className="space-y-4">
      <SectionHead 
        title="Hängende Leases" 
        subtitle="Operator-Auflösung für mehrdeutige Experiment-Zustände" 
      />

      {isLoading ? (
        <div className="text-sm text-[var(--muted-foreground)]">Laden...</div>
      ) : isError ? (
        <Card className="p-8 text-center text-red-800 bg-red-50 border-red-200">
          <div className="font-medium mb-1 flex items-center justify-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Fehler beim Laden
          </div>
          Die Liste konnte NICHT geladen werden, der Stand ist deshalb unbekannt.
          {error instanceof Error ? ` (${error.message})` : ''}
        </Card>
      ) : antwortUnbrauchbar ? (
        <Card className="p-8 text-center text-red-800 bg-red-50 border-red-200">
          <div className="font-medium mb-1 flex items-center justify-center gap-2">
            <ShieldAlert className="w-5 h-5" /> Unerwartete Antwort
          </div>
          Die Antwort des Servers enthielt keine lesbare Liste — der Stand ist
          unbekannt. Das ist ausdruecklich NICHT dasselbe wie "keine haengenden
          Leases".
        </Card>
      ) : displayLeases.length === 0 ? (
        <Card className="p-8 text-center text-[var(--muted-foreground)]">
          Es gibt aktuell keine hängenden Leases. Alles in Ordnung.
        </Card>
      ) : (
        <div className="space-y-4">
          {displayLeases.map(lease => {
            const result = results[lease.id]
            const isSuccess = result?.type === 'success'
            const stand = pruefbarkeit(lease, jetztLautServer())
            
            let purposeText = lease.purpose || 'Unbekannt'
            if (lease.purpose === 'experiment:bid') purposeText = 'Bid-Aenderung'
            else if (lease.purpose === 'experiment:state') purposeText = 'Status-Aenderung'

            const age = new Date(lease.acquiredAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })

            return (
              <Card key={lease.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[var(--foreground)]">Lease #{lease.id}</div>
                    <div className="text-sm text-[var(--muted-foreground)] mt-0.5">
                      Zweck: {purposeText} · Keyword: {lease.keywordId ?? 'n/a'} · Alter: {age}
                    </div>
                  </div>
                  {!isSuccess && (
                    !stand.pruefbar ? (
                      <div className="text-sm text-[var(--muted-foreground)] bg-[var(--muted)] px-3 py-1.5 rounded border border-[var(--border)]">
                        {stand.ersteBeobachtungLiegtVor
                          ? stand.ab
                            ? `Zweite Prüfung ab: ${alsZeit(stand.ab)} — die erste Beobachtung liegt vor, Amazon stand da auf dem Ausgangswert.`
                            : 'Zweite Prüfung ab: Unbekannt — die erste Beobachtung liegt vor, der Termin ist aber nicht lesbar.'
                          : stand.ab
                            ? `Prüfbar ab: ${alsZeit(stand.ab)} (laufender Request könnte noch ankommen)`
                            : 'Prüfbar ab: Unbekannt (laufender Request könnte noch ankommen)'}
                      </div>
                    ) : (
                      <button 
                        onClick={() => handleResolve(lease)}
                        disabled={resolveLease.isPending}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        Pruefen und aufloesen
                      </button>
                    )
                  )}
                </div>
                
                {result && (
                  <div className={`mt-3 p-3 rounded text-sm flex gap-2 items-start ${
                    result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
                    result.type === 'demo' ? 'bg-blue-50 text-blue-800 border border-blue-200' :
                    result.type === 'forbidden' ? 'bg-red-50 text-red-800 border border-red-200' :
                    'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}>
                    {result.type === 'success' && <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                    {(result.type === 'error' || result.type === 'forbidden') && <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                    {result.type === 'demo' && <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                    <span className="leading-relaxed whitespace-pre-wrap">{result.message}</span>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}
    </div>
  )
}
